import { Constructor } from '../../../types/common/types';
import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridGetColumnConfig } from '../types';
import { GridSorterElement } from '@vaadin/vaadin-grid/vaadin-grid-sorter';
import {GridElement, GridSorterDirection} from '@vaadin/vaadin-grid';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { GridEvents } from '../../types/common';

export declare class HasColumnSorterInterface {
  gridSorterHeaderRenderer(
    root: HTMLElement,
    columnName: string,
    columnKey: string,
    config?: GridGetColumnConfig
  ): GridSorterElement;
}

export const HasColumnSorterMixin = <T extends Constructor<BaseWebComponentView>>(superClass: T) => {
  class GridHasColumnSorter extends superClass {
    private sorters = new Map();
    private gridRef!: GridElement;

    connected() {
      super.connected();
      this.gridRef = this.dom.$grid as GridElement;
    }

    private overrideImplicitSortBehavior(sorterPath: string, sorter: GridSorterElement): void {
      this.sorters.set(sorterPath, sorter);
      this.preventGridSortBehavior(sorter);
      this.updateSortersOnChange(sorter);
    }

    private preventGridSortBehavior(sorter: GridSorterElement): void {
      sorter.addEventListener('sorter-changed', event => {
        event.stopPropagation();
      });
    }

    private updateSortersOnChange(sorter: GridSorterElement): void {
      sorter.addEventListener('click', () => {
        this.sorters.forEach((sort: GridSorterElement) => {
          const notCurrentSort = sort.path !== sorter.path;

          if (notCurrentSort) {
            sort.direction = null;
          }
        });
      });
    }

    private sorterHeaderContentRenderer(
      columnName: string,
      sorterPath: string,
      config?: GridGetColumnConfig
    ): GridSorterElement {
      const direction = sorterPath === config?.sortColumnName ? config?.sortDirection : null;
      const sorter = new GridSorterElement();
      sorter.path = sorterPath;

      if (direction) {
        sorter.direction = direction as GridSorterDirection;
      }

      sorter.innerHTML = `<span class="text-semibold cell-label cell-label--header">${columnName}</span>`;

      if (!config?.uiSort) {
        this.overrideImplicitSortBehavior(sorterPath, sorter);
      }

      return sorter;
    }

    private onSorterHeaderClick(event: MouseEvent) {
      const sortedColumn = event.composedPath().find((elem: HTMLElement) => {
        return elem.tagName === 'vaadin-grid-sorter'.toUpperCase();
      }) as GridSorterElement;

      if (isEmpty(sortedColumn) || isEmpty(this.gridRef)) {
        return;
      }

      this.gridRef.dispatchEvent(
        new CustomEvent(GridEvents.GRID_SORTED, {
          bubbles: true,
          detail: {
            columnName: sortedColumn.path,
            direction: sortedColumn.direction,
          },
        })
      );
    }

    public gridSorterHeaderRenderer(
      root: HTMLElement,
      columnName: string,
      columnKey: string,
      config?: GridGetColumnConfig
    ): GridSorterElement {
      let sorter = root.firstElementChild as GridSorterElement;

      if (!sorter) {
        sorter = this.sorterHeaderContentRenderer(columnName, columnKey, config);
      }

      root.addEventListener('click', (e: MouseEvent) => this.onSorterHeaderClick(e));

      return sorter;
    }
  }

  return GridHasColumnSorter as Constructor<HasColumnSorterInterface> & T;
};
